import { defineStore } from 'pinia'
import { ref } from 'vue'

export type FontScale = 100 | 125 | 150

// Масштаб — атрибут на <html>, а в CSS ему соответствует zoom на body.
// font-size на <html> (и в %, и в px) Mobile Safari пересчитывает при
// показе и скрытии панели браузера: rem раздувается, на экране остаётся
// только фон, а значение в localStorage применяет это снова при загрузке.
const FONT_SCALE_KEY = 'a11y-font-scale'
const MAX_SANE_ROOT_PX = 32
// 150% — это 1.4, не 1.5: zoom: 1.5 на body Mobile Safari не рисует.
const ZOOM_BY_SCALE: Record<FontScale, string> = {
  100: '',
  125: '1.25',
  150: '1.4'
}
const HIGH_CONTRAST_KEY = 'a11y-high-contrast'
const REDUCE_MOTION_KEY = 'a11y-reduce-motion'

export const useAccessibilityStore = defineStore('accessibility', () => {
  const fontScale = ref<FontScale>(100)
  let guardBound = false
  const highContrast = ref(false)
  const reduceMotion = ref(false)

  const applySettings = () => {
    const html = document.documentElement

    // Инлайн font-size с прошлой версии или от автосайза Safari снимаем всегда:
    // иначе он снова раздувается при следующем изменении вьюпорта.
    html.style.removeProperty('font-size')
    const zoom = ZOOM_BY_SCALE[fontScale.value]
    if (zoom) document.body.style.zoom = zoom
    else document.body.style.removeProperty('zoom')

    if (fontScale.value === 100) {
      html.removeAttribute('data-a11y-font-scale')
    } else {
      html.setAttribute('data-a11y-font-scale', String(fontScale.value))
    }

    if (highContrast.value) {
      html.setAttribute('data-a11y-contrast', 'high')
    } else {
      html.removeAttribute('data-a11y-contrast')
    }

    if (reduceMotion.value) {
      html.setAttribute('data-a11y-reduce-motion', 'true')
    } else {
      html.removeAttribute('data-a11y-reduce-motion')
    }
  }

  const init = () => {
    const savedScale = Number(localStorage.getItem(FONT_SCALE_KEY))
    if (savedScale === 125 || savedScale === 150) {
      fontScale.value = savedScale
    }
    highContrast.value = localStorage.getItem(HIGH_CONTRAST_KEY) === 'true'
    reduceMotion.value = localStorage.getItem(REDUCE_MOTION_KEY) === 'true'
    applySettings()
    bindRootFontGuard()
    guardRootFont()
  }

  const guardRootFont = () => {
    const html = document.documentElement
    if (html.style.fontSize) html.style.removeProperty('font-size')
    const zoom = parseFloat(getComputedStyle(document.body).zoom) || 1
    if (zoom >= 1.48) document.body.style.zoom = '1.4'
    const used = parseFloat(getComputedStyle(html).fontSize) || 16
    if (used <= MAX_SANE_ROOT_PX) return
    fontScale.value = 100
    localStorage.removeItem(FONT_SCALE_KEY)
    html.removeAttribute('data-a11y-font-scale')
    html.style.removeProperty('font-size')
    document.body.style.removeProperty('zoom')
  }

  const bindRootFontGuard = () => {
    if (guardBound) return
    guardBound = true
    window.addEventListener('resize', guardRootFont)
    window.addEventListener('orientationchange', guardRootFont)
    window.visualViewport?.addEventListener('resize', guardRootFont)
  }

  const setFontScale = (scale: FontScale) => {
    fontScale.value = scale
    localStorage.setItem(FONT_SCALE_KEY, String(scale))
    applySettings()
  }

  const toggleHighContrast = () => {
    highContrast.value = !highContrast.value
    localStorage.setItem(HIGH_CONTRAST_KEY, String(highContrast.value))
    applySettings()
  }

  const toggleReduceMotion = () => {
    reduceMotion.value = !reduceMotion.value
    localStorage.setItem(REDUCE_MOTION_KEY, String(reduceMotion.value))
    applySettings()
  }

  const reset = () => {
    fontScale.value = 100
    highContrast.value = false
    reduceMotion.value = false
    localStorage.removeItem(FONT_SCALE_KEY)
    localStorage.removeItem(HIGH_CONTRAST_KEY)
    localStorage.removeItem(REDUCE_MOTION_KEY)
    applySettings()
  }

  return {
    fontScale,
    highContrast,
    reduceMotion,
    init,
    setFontScale,
    toggleHighContrast,
    toggleReduceMotion,
    reset
  }
})
